// In src/components/SermonModal.js

import React, { useState, useEffect } from 'react';
// eslint-disable-next-line no-unused-vars
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, IconButton, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

// eslint-disable-next-line no-unused-vars
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 450,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

function SermonModal({ open, onClose, onSave, sermon: initialSermon }) {
  const [title, setTitle] = useState('');
  const [speaker, setSpeaker] = useState('');
  const [date, setDate] = useState(null);
  const [description, setDescription] = useState('');
  const [videoLink, setVideoLink] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialSermon) {
      setTitle(initialSermon.title || '');
      setSpeaker(initialSermon.speaker || '');
      setDate(initialSermon.date ? initialSermon.date.toDate() : null);
      setDescription(initialSermon.description || '');
      setVideoLink(initialSermon.videoLink || '');
    } else {
      setTitle('');
      setSpeaker('');
      setDate(null);
      setDescription('');
      setVideoLink('');
    }
    setErrors({});
  }, [initialSermon, open]);

  const validate = () => {
    let tempErrors = {};
    let isValid = true;

    if (!title) {
      tempErrors.title = "Title is required.";
      isValid = false;
    }
    if (!speaker) {
      tempErrors.speaker = "Speaker is required.";
      isValid = false;
    }
    if (!date) {
      tempErrors.date = "Date is required.";
      isValid = false;
    }
    if (!videoLink) {
      tempErrors.videoLink = "Video link is required.";
      isValid = false;
    } else if (!/^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_.~#?&//=]*)$/.test(videoLink)) {
      tempErrors.videoLink = "Please enter a valid URL.";
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    const sermonData = {
      title,
      speaker,
      date,
      description,
      videoLink,
    };

    try {
      await onSave(sermonData);
      onClose();
    } catch (error) {
      console.error("Error saving sermon:", error);
      // Handle error display to user if necessary
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>
          {initialSermon ? 'Edit Sermon' : 'Add New Sermon'}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            margin="dense"
            label="Sermon Title"
            type="text"
            fullWidth
            variant="outlined"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            error={!!errors.title}
            helperText={errors.title}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Speaker"
            type="text"
            fullWidth
            variant="outlined"
            value={speaker}
            onChange={(e) => setSpeaker(e.target.value)}
            error={!!errors.speaker}
            helperText={errors.speaker}
            sx={{ mb: 2 }}
          />
          <DatePicker
            label="Date of Sermon"
            value={date}
            onChange={(newValue) => setDate(newValue)}
            slots={{ textField: TextField }}
            slotProps={{ textField: { fullWidth: true, margin: "dense", sx: { mb: 2 }, error: !!errors.date, helperText: errors.date } }}
            enableAccessibleFieldDOMStructure={false} // ADDED THIS PROP
          />
          <TextField
            margin="dense"
            label="Description (Optional)"
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Video Link (e.g., Facebook Live URL)"
            type="url"
            fullWidth
            variant="outlined"
            value={videoLink}
            onChange={(e) => setVideoLink(e.target.value)}
            error={!!errors.videoLink}
            helperText={errors.videoLink}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} sx={{ mr: 1 }}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {initialSermon ? 'Save Changes' : 'Add Sermon'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}

export default SermonModal;